/**
 * Maps the new AutocompleteSuggestion format to the legacy AutocompletePrediction format
 * to maintain compatibility with existing code that expects the old API structure.
 * based on docs:
 * - PlacePrediction: https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#PlacePrediction
 * - AutocompletePrediction: https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
 */
export function mapAutocompleteSuggestionToPrediction(
  suggestion: google.maps.places.AutocompleteSuggestion,
  input: string,
): google.maps.places.AutocompletePrediction {
  const normalizedInput = input.trim().toLowerCase();
  const placePrediction = suggestion.placePrediction;

  if (!placePrediction) {
    throw new Error('AutocompleteSuggestion does not contain placePrediction');
  }

  const mainText = placePrediction.mainText?.toString() || '';
  const secondaryText = placePrediction.secondaryText?.toString() || '';
  const fullText = placePrediction.text?.toString() || '';

  const description = fullText || (secondaryText ? `${mainText}, ${secondaryText}` : mainText);

  const terms = parseTermsFromText(description);

  const main_text_matched_substrings = calculateMatchedSubstrings(normalizedInput, mainText);
  const secondary_text_matched_substrings = calculateMatchedSubstrings(
    normalizedInput,
    secondaryText,
  );
  const matched_substrings = calculateMatchedSubstrings(normalizedInput, description);

  return {
    description,
    matched_substrings: matched_substrings || [],
    place_id: placePrediction.placeId || '',
    structured_formatting: {
      main_text: mainText,
      main_text_matched_substrings,
      secondary_text: secondaryText,
      secondary_text_matched_substrings,
    } as google.maps.places.StructuredFormatting,
    terms,
    types: placePrediction.types || [],
  };
}

function parseTermsFromText(text: string): google.maps.places.PredictionTerm[] {
  if (!text) return [];

  const parts = text.split(',').map((part) => part.trim());
  let offset = 0;
  const terms: google.maps.places.PredictionTerm[] = [];

  parts.forEach((part, index) => {
    if (part) {
      terms.push({
        offset,
        value: part,
      });
      offset += part.length + (index < parts.length - 1 ? 2 : 0);
    }
  });

  return terms;
}

function calculateMatchedSubstrings(
  input: string,
  text: string,
): google.maps.places.PredictionSubstring[] | undefined {
  if (!input || !text) return [];

  const normalizedInput = input.toLowerCase();

  // Split input into words
  const inputWords = normalizedInput.split(/\s+/).filter((word) => word.length > 0);

  // Parse text into terms (comma-separated parts) with their offsets
  const terms = parseTermsForMatching(text);
  const matchedTermIndices = new Set<number>();
  const matches: google.maps.places.PredictionSubstring[] = [];

  // Match each input word against term starts
  for (const word of inputWords) {
    // Find the first unmatched term that starts with this word
    for (let i = 0; i < terms.length; i++) {
      if (matchedTermIndices.has(i)) continue;

      const termValue = terms[i].value.toLowerCase();
      if (termValue.startsWith(word)) {
        matches.push({
          length: word.length,
          offset: terms[i].offset,
        });
        matchedTermIndices.add(i);
        break;
      }
    }
  }

  if (matches.length < 1) {
    return undefined;
  }

  return matches.sort((a, b) => a.offset - b.offset);
}

function parseTermsForMatching(text: string): Array<{ offset: number; value: string }> {
  if (!text) return [];

  const parts = text.split(',').map((part) => part.trim());
  let offset = 0;
  const terms: Array<{ offset: number; value: string }> = [];

  parts.forEach((part, index) => {
    if (part) {
      terms.push({
        offset,
        value: part,
      });
      // Add length of part + comma + space for next offset
      offset += part.length + (index < parts.length - 1 ? 2 : 0);
    }
  });

  return terms;
}
